import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const LS_KEY = "zm_menu_config";
const LS_HOME = "zm_home_layout";
const MenuConfigContext = createContext(null);

function readLastKnown() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
function readHomeLastKnown() {
  try {
    const raw = localStorage.getItem(LS_HOME);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function MenuConfigProvider({ children }) {
  const [config, setConfig] = useState(() => readLastKnown());
  const [homeLayout, setHomeLayout] = useState(() => readHomeLastKnown());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/menu-config");
      if (data && data.quick_actions && data.bottom_nav) {
        setConfig(data);
        try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
      }
    } catch (e) { /* 비차단: 기존/기본값 유지 */ }
    try {
      const { data } = await api.get("/home-layout");
      if (data && Array.isArray(data.blocks)) {
        setHomeLayout(data);
        try { localStorage.setItem(LS_HOME, JSON.stringify(data)); } catch (e) { /* noop */ }
      }
    } catch (e) { /* 비차단 */ }
  }, []);

  useEffect(() => {
    refresh();
    let last = 0;
    const onFocus = () => {
      const now = Date.now();
      if (now - last < 10000) return;
      last = now;
      refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onFocus();
    });
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <MenuConfigContext.Provider value={{ config, homeLayout, refresh }}>
      {children}
    </MenuConfigContext.Provider>
  );
}

// 코드가 소유한 항목(items: [{key, ...}])을 config 순서/표시로 정렬·필터.
// config 없으면 코드 기본 순서 그대로(시각 회귀 0).
export function useMenuItems(group, items) {
  const ctx = useContext(MenuConfigContext);
  const cfg = ctx?.config?.[group];
  if (!cfg || !Array.isArray(cfg)) return items;
  const orderMap = new Map(cfg.map((c) => [c.key, c]));
  const visible = items
    .filter((it) => {
      const c = orderMap.get(it.key);
      return c ? c.visible !== false : true; // config에 없는(새) 메뉴는 노출
    })
    .sort((a, b) => {
      const oa = orderMap.has(a.key) ? orderMap.get(a.key).order : 9999;
      const ob = orderMap.has(b.key) ? orderMap.get(b.key).order : 9999;
      return oa - ob;
    });
  return visible;
}

// 홈 블록 순서/크기/표시. config 없으면 null(→ 기본 렌더). 반환: { order: [keys], sizeOf(key), isVisible(key) }
export function useHomeLayout() {
  const ctx = useContext(MenuConfigContext);
  const blocks = ctx?.homeLayout?.blocks;
  if (!blocks || !Array.isArray(blocks)) return null;
  const byKey = new Map(blocks.map((b) => [b.key, b]));
  const order = [...blocks].sort((a, b) => a.order - b.order).map((b) => b.key);
  return {
    order,
    sizeOf: (key) => byKey.get(key)?.size || "medium",
    isVisible: (key) => (byKey.has(key) ? byKey.get(key).visible !== false : true),
  };
}
